/**
 * Currency formatting utilities
 *
 * Provides consistent currency formatting across the application
 * supporting multiple currencies and locales.
 */

/**
 * Get currency symbol from ISO 4217 currency code
 */
export function getCurrencySymbol(currencyCode: string): string {
  const symbols: Record<string, string> = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    JPY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'CHF',
    CNY: '¥',
    INR: '₹',
    NZD: 'NZ$',
    SGD: 'S$',
    HKD: 'HK$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RON: 'lei',
    BGN: 'лв',
    TRY: '₺',
    ILS: '₪',
    ZAR: 'R',
    BRL: 'R$',
    MXN: 'MX$',
    ARS: 'AR$',
    CLP: 'CL$',
    COP: 'CO$',
    KRW: '₩',
    THB: '฿',
    MYR: 'RM',
    PHP: '₱',
    IDR: 'Rp',
    VND: '₫',
    RUB: '₽',
    UAH: '₴',
    AED: 'د.إ',
    SAR: '﷼'
  }

  return symbols[currencyCode.toUpperCase()] || currencyCode
}

/**
 * Format amount with currency symbol
 *
 * @param amount - The amount to format
 * @param currencyCode - ISO 4217 currency code (e.g., 'GBP', 'USD', 'EUR')
 * @param locale - Optional locale for number formatting (defaults to browser locale)
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(125.00, 'GBP') // "£125"
 * formatCurrency(99.99, 'USD') // "$99.99"
 * formatCurrency(1234.56, 'EUR') // "€1,234.56"
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  locale?: string
): string {
  try {
    // Use Intl.NumberFormat for proper locale-aware formatting
    const formatter = new Intl.NumberFormat(locale || navigator.language, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    })

    return formatter.format(amount)
  } catch (error) {
    // Fallback to simple symbol + amount formatting
    const symbol = getCurrencySymbol(currencyCode)
    const formattedAmount = amount.toFixed(amount % 1 === 0 ? 0 : 2)
    return `${symbol}${formattedAmount}`
  }
}

/**
 * Format amount with currency symbol (simple version without locale formatting)
 *
 * @param amount - The amount to format
 * @param currencyCode - ISO 4217 currency code
 * @returns Simple formatted currency string (e.g., "£125", "$99.99")
 */
export function formatCurrencySimple(amount: number, currencyCode: string): string {
  const symbol = getCurrencySymbol(currencyCode)
  const formattedAmount = amount % 1 === 0 ? amount.toString() : amount.toFixed(2)
  return `${symbol}${formattedAmount}`
}

/**
 * Get locale code from currency code
 * Maps common currency codes to their typical locale
 */
export function getLocaleFromCurrency(currencyCode: string): string {
  const locales: Record<string, string> = {
    GBP: 'en-GB',
    USD: 'en-US',
    EUR: 'de-DE', // Default to German formatting for EUR
    JPY: 'ja-JP',
    AUD: 'en-AU',
    CAD: 'en-CA',
    CHF: 'de-CH',
    CNY: 'zh-CN',
    INR: 'en-IN',
    NZD: 'en-NZ',
    SGD: 'en-SG',
    HKD: 'zh-HK',
    SEK: 'sv-SE',
    NOK: 'nb-NO',
    DKK: 'da-DK',
    PLN: 'pl-PL',
    CZK: 'cs-CZ',
    HUF: 'hu-HU',
    RON: 'ro-RO',
    BGN: 'bg-BG',
    TRY: 'tr-TR',
    ILS: 'he-IL',
    ZAR: 'en-ZA',
    BRL: 'pt-BR',
    MXN: 'es-MX',
    KRW: 'ko-KR',
    THB: 'th-TH',
    RUB: 'ru-RU'
  }

  return locales[currencyCode.toUpperCase()] || 'en-US'
}
